import { dataService } from '@/services/data-service';
import { LocationEvent, WorkOrder, Association, User, LocationEventType, LocationData } from '@/types/models';
import { calculateDistance } from '@/utils/distance';
import { logger } from '@/utils/logger';

// Configuration constants
const CHECKIN_THRESHOLD_METERS = 805; // 0.5 miles in meters
const CHECKOUT_THRESHOLD_METERS = 8047; // 5 miles in meters
const MIN_TIME_BETWEEN_EVENTS = 15 * 60 * 1000; // 15 minutes to prevent duplicate events

export interface CheckinTarget {
	type: 'work_order' | 'association';
	workOrder?: WorkOrder;
	association: Association;
	distance: number;
	latitude: number;
	longitude: number;
}

export interface CheckinResult {
	success: boolean;
	message: string;
	event?: LocationEvent;
	target?: CheckinTarget;
	// Additional properties used in the app
	eventType?: string;
}

// Alias for backward compatibility
export type CheckinEvent = CheckinResult;

class AutoCheckinService {
	private isMonitoring = false;
	private monitoringInterval: ReturnType<typeof setInterval> | null = null;
	private onCheckInOutCallback?: (result: CheckinResult) => void;

	/**
	 * Start location monitoring for automatic check-ins
	 */
	startMonitoring(user: User, onCheckInOut?: (result: CheckinResult) => void): void {
		if (this.isMonitoring) {
			logger.log('Auto-checkin monitoring already active');
			// Update callback if provided
			if (onCheckInOut) {
				this.onCheckInOutCallback = onCheckInOut;
			}
			return;
		}

		// Validate user before starting
		if (!user || !user.id) {
			console.error('Invalid user provided to startMonitoring');
			return;
		}

		logger.log('Starting auto-checkin monitoring for user:', user.id);
		this.isMonitoring = true;
		this.onCheckInOutCallback = onCheckInOut;

		// Note: We no longer poll for location here.
		// Location updates are pushed from LocationService via processLocationUpdate.
	}

	/**
	 * Process a new location update from the LocationService
	 */
	async processLocationUpdate(user: User, location: LocationData): Promise<void> {
		if (!this.isMonitoring) return;

		try {
			// 1. Check for auto-checkout (if we moved away from current site)
			const checkoutResult = await this.checkForAutoCheckout(user, location.latitude, location.longitude);
			if (checkoutResult) {
				if (this.onCheckInOutCallback) {
					this.onCheckInOutCallback(checkoutResult);
				}
				// If we just checked out, we can proceed to check if we should check in somewhere else
			}

			// 2. Find nearby targets
			const targets = await this.findNearbyTargets(location.latitude, location.longitude);
			if (targets.length === 0) return;

			// Sort by distance
			targets.sort((a, b) => a.distance - b.distance);
			const bestTarget = targets[0];

			// 3. Check if we are already checked in
			const recentEvents = await this.getRecentEvents(user, 5);

			// Find the most recent event (check-in or check-out)
			// Assuming getRecentEvents returns sorted descending by timestamp
			const lastEvent = recentEvents[0];

			// If we are currently checked in
			if (lastEvent && lastEvent.type === LocationEventType.CheckIn) {
				// If checked in to the same place, do nothing
				if (lastEvent.associationId === bestTarget.association.id) {
					return;
				}

				// If checked in elsewhere, we generally don't auto-switch unless we checked out first.
				// (Auto-checkout should have happened in step 1 if we are far enough)
				return;
			}

			// 4. Perform auto-checkin
			logger.log(`Auto-checking in to ${bestTarget.association.name} (${bestTarget.distance.toFixed(0)}m)`);

			const checkinEvent = await dataService.createLocationEvent({
				type: LocationEventType.CheckIn,
				lat: location.latitude,
				lng: location.longitude,
				accuracy: location.accuracy,
				userId: user.id,
				associationId: bestTarget.association.id,
				workOrderId: bestTarget.workOrder?.id,
				timestamp: new Date().toISOString(),
			});

			const result: CheckinResult = {
				success: true,
				message: `Automatically checked in to ${bestTarget.association.name}`,
				event: checkinEvent,
				target: bestTarget,
			};

			if (this.onCheckInOutCallback) {
				this.onCheckInOutCallback(result);
			}

		} catch (error) {
			console.error('Error processing location update:', error);
		}
	}

	/**
	 * Stop location monitoring
	 */
	stopMonitoring(): void {
		if (!this.isMonitoring) {
			return;
		}

		logger.log('Stopping auto-checkin monitoring');
		this.isMonitoring = false;

		if (this.monitoringInterval) {
			clearInterval(this.monitoringInterval);
			this.monitoringInterval = null;
		}
	}

	/**
	 * Find potential check-in targets near the user's location
	 */
	async findNearbyTargets(userLat: number, userLng: number): Promise<CheckinTarget[]> {
		const targets: CheckinTarget[] = [];

		try {
			// Get active work orders
			const workOrders = await dataService.getWorkOrders({
				status: 'in_progress',
			});

			// Get active associations
			const associations = await dataService.getAssociations({
				status: 'active',
			});

			// Check work orders first (higher priority)
			for (const workOrder of workOrders.data) {
				if (workOrder.associationId) {
					const association = associations.data.find(a => a.id === workOrder.associationId);
					if (association && association.lat && association.lng) {
						const distance = calculateDistance(
							userLat,
							userLng,
							association.lat,
							association.lng,
						);

						if (distance <= CHECKIN_THRESHOLD_METERS) {
							targets.push({
								type: 'work_order',
								workOrder,
								association,
								distance,
								latitude: association.lat,
								longitude: association.lng,
							});
						}
					}
				}
			}

			// Check remaining associations if no work order targets found
			if (targets.length === 0) {
				for (const association of associations.data) {
					if (association.lat && association.lng) {
						const distance = calculateDistance(
							userLat,
							userLng,
							association.lat,
							association.lng,
						);

						if (distance <= CHECKIN_THRESHOLD_METERS) {
							targets.push({
								type: 'association',
								association,
								distance,
								latitude: association.lat,
								longitude: association.lng,
							});
						}
					}
				}
			}

			// Sort by distance (closest first)
			targets.sort((a, b) => a.distance - b.distance);

		} catch (error) {
			console.error('Error finding nearby targets:', error);
		}

		return targets;
	}

	/**
	 * Check if user should be automatically checked out
	 */
	async checkForAutoCheckout(user: User, userLat: number, userLng: number): Promise<CheckinResult | null> {
		try {
			// Get recent check-in events for this user
			const recentEvents = await dataService.getLocationEvents(user.id, undefined, {
				limit: 10,
				sortBy: 'timestamp',
				sortOrder: 'desc',
			});

			// Find the most recent check-in without a matching check-out
			let lastCheckinEvent: LocationEvent | null = null;
			const eventsByAssociation = new Map<string, LocationEvent[]>();

			for (const event of recentEvents.data) {
				if (!event.associationId) continue;

				if (!eventsByAssociation.has(event.associationId)) {
					eventsByAssociation.set(event.associationId, []);
				}
				eventsByAssociation.get(event.associationId)!.push(event);
			}

			// Check each association for unclosed check-ins
			for (const [associationId, events] of eventsByAssociation) {
				const sortedEvents = events.sort((a, b) =>
					new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
				);

				if (sortedEvents.length > 0 && sortedEvents[0].type === 'check_in') {
					lastCheckinEvent = sortedEvents[0];
					break;
				}
			}

			if (!lastCheckinEvent || !lastCheckinEvent.associationId) {
				return null; // No active check-in found
			}

			// Get the association for the last check-in
			const association = await dataService.getAssociation(lastCheckinEvent.associationId);
			if (!association.lat || !association.lng) {
				return null;
			}

			// Calculate distance from current location to association
			const distance = calculateDistance(
				userLat,
				userLng,
				association.lat,
				association.lng,
			);

			// Check if user is far enough away to trigger auto-checkout
			if (distance > CHECKOUT_THRESHOLD_METERS) {
				// Create checkout event
				const checkoutEvent = await dataService.createLocationEvent({
					type: LocationEventType.CheckOut,
					lat: userLat,
					lng: userLng,
					accuracy: 5,
					userId: user.id,
					associationId: association.id,
					timestamp: new Date().toISOString(),
				});

				return {
					success: true,
					message: `Automatically checked out of ${association.name} (${(distance / 1000).toFixed(1)}km away)`,
					event: checkoutEvent,
				};
			}

		} catch (error) {
			console.error('Error checking for auto-checkout:', error);
		}

		return null;
	}

	/**
	 * Get monitoring status
	 */
	isCurrentlyMonitoring(): boolean {
		return this.isMonitoring;
	}

	/**
	 * Get configuration constants
	 */
	getConfig() {
		return {
			checkinThresholdMeters: CHECKIN_THRESHOLD_METERS,
			checkoutThresholdMeters: CHECKOUT_THRESHOLD_METERS,
			minTimeBetweenEvents: MIN_TIME_BETWEEN_EVENTS,
		};
	}

	/**
	 * Get recent location events for a user
	 */
	async getRecentEvents(user: User, limit: number = 20): Promise<LocationEvent[]> {
		try {
			const response = await dataService.getLocationEvents(user.id, undefined, {
				limit,
				sortBy: 'timestamp',
				sortOrder: 'desc',
			});
			return response.data;
		} catch (error) {
			console.error('Error getting recent events:', error);
			return [];
		}
	}

	/**
	 * Check if the service is currently running
	 */
	isServiceRunning(): boolean {
		return this.isMonitoring;
	}

	/**
	 * Perform a manual check for check-in/out opportunities
	 */
	async manualCheck(): Promise<CheckinResult[]> {
		try {
			// This is a placeholder implementation
			// In a real app, you would:
			// 1. Get current location
			// 2. Check for nearby targets
			// 3. Perform check-in/out as needed
			logger.log('Manual check requested');
			return [];
		} catch (error) {
			console.error('Error during manual check:', error);
			return [];
		}
	}

	/**
	 * Get the current service status
	 */
	getServiceStatus(): { isRunning: boolean; isMonitoring: boolean } {
		return {
			isRunning: this.isMonitoring,
			isMonitoring: this.isMonitoring,
		};
	}
}

export const autoCheckinService = new AutoCheckinService();
